// The strip above the listing. It counts what is listed, with a "+" while more is still to come
// as it scrolls, and offers a new folder, until something is selected: then it gives way to the
// selection bar, which says how much is selected and offers what can be done to all of it at once —
// the same things each item's own menu offers, so in the drive to download it, move it or send it
// to Trash, and in Trash to restore it or delete it for good.
//
// Inside an open Locked it also says when Locked reseals itself, counting down, and offers its
// settings and to lock it now. Hidden, it says so too, since the rail entry will be gone again
// once it reseals.
//
// On a phone its buttons and chips are icons alone, with the countdown's time still showing.
import PropTypes from 'prop-types'

import ClearIcon from '@mui/icons-material/CloseRounded'
import DeleteForeverIcon from '@mui/icons-material/DeleteForeverOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import ClockIcon from '@mui/icons-material/ScheduleRounded'
import HiddenIcon from '@mui/icons-material/VisibilityOffOutlined'
import IconButton from '@mui/material/IconButton'
import LockIcon from '@mui/icons-material/LockOutlined'
import MoveIcon from '@mui/icons-material/DriveFileMoveOutlined'
import NewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined'
import RestoreIcon from '@mui/icons-material/RestoreFromTrashOutlined'
import SettingsIcon from '@mui/icons-material/SettingsOutlined'
import TrashIcon from '@mui/icons-material/DeleteOutlineRounded'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import {BaseButton} from '../shared/blocks/Buttons'

import './driveToolbar.scss'

const SelectionBar = ({count, onClear, onDownload, onMove, onSeal, sealsOut = false, onTrash, onRestore, onPurge}) => (
  <div className='drive-toolbar selecting' role='toolbar' aria-label='Selection'>
    <IconButton className='selection-clear' aria-label='Clear selection' onClick={onClear}>
      <ClearIcon fontSize='small' />
    </IconButton>
    <span className='selection-count'>{count} selected</span>
    <span className='toolbar-spacer' />
    {onDownload && (
      <BaseButton className='selection-quiet' variant='text' onClick={onDownload}>
        <DownloadIcon fontSize='small' />Download
      </BaseButton>
    )}
    {onRestore && (
      <BaseButton className='selection-quiet' variant='text' onClick={onRestore}>
        <RestoreIcon fontSize='small' />Restore
      </BaseButton>
    )}
    {onMove && (
      <BaseButton className='selection-quiet' variant='text' onClick={onMove}>
        <MoveIcon fontSize='small' />Move
      </BaseButton>
    )}
    {onSeal && (
      <BaseButton className='selection-locked' variant='text' color='secondary' onClick={onSeal}>
        {sealsOut ? <UnlockIcon fontSize='small' /> : <LockIcon fontSize='small' />}
        {sealsOut ? 'Move out of Locked' : 'Move to Locked'}
      </BaseButton>
    )}
    {onTrash && (
      <BaseButton className='selection-danger' variant='text' color='error' onClick={onTrash}>
        <TrashIcon fontSize='small' />Delete
      </BaseButton>
    )}
    {onPurge && (
      <BaseButton className='selection-danger' variant='text' color='error' onClick={onPurge}>
        <DeleteForeverIcon fontSize='small' />Delete forever
      </BaseButton>
    )}
  </div>
)

const selectionShape = {
  count: PropTypes.number.isRequired,
  onClear: PropTypes.func.isRequired,
  // In the drive. Download is absent when nothing selected can be saved: folders, and files still
  // being secured.
  onDownload: PropTypes.func,
  onMove: PropTypes.func,
  // Into Locked, or out of it when "sealsOut".
  onSeal: PropTypes.func,
  sealsOut: PropTypes.bool,
  onTrash: PropTypes.func,
  // In Trash.
  onRestore: PropTypes.func,
  onPurge: PropTypes.func
}

SelectionBar.propTypes = selectionShape

const clock = seconds => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`

const LockedControls = ({remaining, hidden, onSettings, onLockNow}) => (
  <div className='toolbar-locked'>
    {hidden && (
      <span className='toolbar-chip' title='Hidden when sealed'>
        <HiddenIcon fontSize='small' /><span className='toolbar-label'>Hidden when sealed</span>
      </span>
    )}
    <span className='toolbar-chip' role='timer' aria-live='off' title='Relocks in'>
      <ClockIcon fontSize='small' /><span className='toolbar-label'>Relocks in</span> <span className='chip-clock'>{clock(remaining)}</span>
    </span>
    <BaseButton className='toolbar-quiet' variant='text' aria-label='Settings' onClick={onSettings}>
      <SettingsIcon fontSize='small' /><span className='toolbar-label'>Settings</span>
    </BaseButton>
    <BaseButton className='toolbar-quiet' variant='text' aria-label='Lock now' onClick={onLockNow}>
      <LockIcon fontSize='small' /><span className='toolbar-label'>Lock now</span>
    </BaseButton>
  </div>
)

const lockedShape = {
  // Seconds before Locked reseals itself.
  remaining: PropTypes.number.isRequired,
  hidden: PropTypes.bool.isRequired,
  onSettings: PropTypes.func.isRequired,
  onLockNow: PropTypes.func.isRequired
}

LockedControls.propTypes = lockedShape

const DriveToolbar = ({count, more = false, onNewFolder, selection, locked = null}) => {
  if (selection) return <SelectionBar {...selection} />

  return (
    <div className='drive-toolbar'>
      <span className='toolbar-count'>{count}{more && '+'} item{count === 1 && !more ? '' : 's'}</span>
      {locked && <LockedControls {...locked} />}
      {onNewFolder && (
        <BaseButton className='toolbar-new-folder' variant='outlined' aria-label='New folder' onClick={onNewFolder}>
          <NewFolderIcon fontSize='small' /><span className='toolbar-label'>New folder</span>
        </BaseButton>
      )}
    </div>
  )
}

DriveToolbar.propTypes = {
  count: PropTypes.number.isRequired,
  // Whether more files follow those counted, still to be listed.
  more: PropTypes.bool,
  // Absent inside a folder: folders do not nest, so one made there would land at the top instead.
  onNewFolder: PropTypes.func,
  // Present while anything is selected.
  selection: PropTypes.shape(selectionShape),
  // Inside Locked while it is open.
  locked: PropTypes.shape(lockedShape)
}

export default DriveToolbar
